import { Component, OnDestroy, ViewChild, ElementRef, ChangeDetectorRef, OnInit, AfterViewInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';

import { Subject, Subscription, filter, exhaustMap, forkJoin, Observable, finalize, catchError, of, debounceTime, tap } from 'rxjs';

import { UserPreferencesService, UserAppMessageTypeEnum } from '../../../../Account/UserPreferences/services/user-preferences.service';
import { ScrollPanelComponent } from '../../../../Common/Basic/components/ScrollPanel/scroll-panel.component';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { userPermissionsEnum } from '../../../../Core/Users/enums/user-permissions.enum';
import { UserAppPreferencesModel } from '../../../../Core/Users/models/user-app-preferences.model';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { deepCompare } from '../../../../Core/utils';
import { MonitorUserPreferencesComponent } from '../MonitorUserPreferences/monitor-user-preferences.component';
import type { DiscoverUserPreferencesComponent } from '../DiscoverUserPreferences/discover-user-preferences.component';
import { ReportUserPreferencesComponent } from '../ReportUserPreferences/report-user-preferences.component';
import { GeneralPreferencesComponent } from '../GeneralPreferences/general-preferences.component';
import { PreferencesModel } from '../../../../Core/Users/models/preferences.model';
import { DataLanguageModel } from '../../../../Core/Users/models/data-language.model';
import { ConfirmDialogModel } from '../../../../Common/Dialogs/models/confirm-dialog.model';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { ConfirmDialogComponent } from '../../../../Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { UserCurrency } from '../../../../Core/Users/enums/user-currency';
import { FooterModel } from '../../../../Common/TargetingShared/model/footer.model';
import { ActionKeys } from '../../../../Common/Forms/enums/keys.enum';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { TabComponent } from '../../../../Common/Layout/components/Tab/tab.component';
import { FAVORITE_COLORS_SECTION_ID } from '@Common/Forms/components/AdvancedColorPickerPopover/service/favorite-colors.service';

// TODO: Make this component use angular forms (template or reactive)
export type SectionListItems = { label: string; value: ElementRef }[];

type UserPrefsListItemsKey = keyof Pick<UserPreferencesComponent, 'generalListItems' | 'monitorListItems' | 'discoverListItems' | 'reportListItems'>;

interface UserPreferences {
    appPreferences: UserAppPreferencesModel;
    generalPreferences?: PreferencesModel;
    userAppMessageTypeEnum?: UserAppMessageTypeEnum;
}

enum UserPreferencesTabIds {
    GENERAL = 'userPreferences.generalTab',
    DISCOVER = 'userPreferences.discoverTab',
    MONITOR = 'userPreferences.monitorTab',
    REPORT = 'userPreferences.reportTab'
}

@Component({
    selector: 'agpr-user-preferences',
    templateUrl: './user-preferences.component.html',
    styleUrls: ['./user-preferences.component.scss'],
    standalone: false
})
export class UserPreferencesComponent implements OnInit, OnDestroy, AfterViewInit {
    // for left side bar
    generalListItems: SectionListItems = [];
    monitorListItems: SectionListItems = [];
    discoverListItems: SectionListItems = [];
    reportListItems: SectionListItems = [];
    readonly buttonTypes = ButtonTypesEnum;

    show: boolean = false;
    hasMonitoring: boolean = false;
    hasTargeting: boolean = false;
    hasReports: boolean = false;
    loading: boolean = false;
    selectedTabIndex: number = 0;
    selectedTabId: string;
    tabIds = UserPreferencesTabIds;
    id: string = 'userPreferences';

    childErrors: ElementRef[] = [];

    userAppPreferences: UserAppPreferencesModel;
    cachedUserAppPreferences: UserAppPreferencesModel;
    generalPreferences: PreferencesModel;
    isDirty: boolean = false;

    appPreferencesDirty: boolean = false;
    generalPreferencesDirty: boolean = false;

    private subscriptions: Subscription[] = [];
    private targetingPrefsComponent: DiscoverUserPreferencesComponent;
    private monitoringPrefsComponent: MonitorUserPreferencesComponent;
    private reportPrefsComponent: ReportUserPreferencesComponent;
    private generalPrefsComponent: GeneralPreferencesComponent;
    private saveAction$ = new Subject<UserPreferences>();
    private saveStream$ = this.saveAction$.pipe(
        exhaustMap((prefs) => {
            const obs: Observable<void>[] = [];
            let error;
            if (prefs.appPreferences) {
                obs.push(this.userPreferencesService.saveChanges(prefs.appPreferences, prefs?.userAppMessageTypeEnum));
            }
            if (prefs.generalPreferences) {
                obs.push(this.userPreferencesService.updateGeneralPreferences(prefs.generalPreferences, !!prefs.appPreferences));
            }
            // Kept by agents-service rather than the preferences record, so it saves alongside them
            obs.push(this.generalPrefsComponent?.saveAutomationAgentQuotaWarnings() ?? of(null));

            return forkJoin(obs).pipe(
                catchError((err) => {
                    error = err;
                    return of(null);
                }),
                finalize(() => {
                    // we want to reload the page when all is complete, before it displaying the error growl message in firefox because
                    // it would reload when distributionApi.setDefaultFooter was still being returned
                    // before we were reloading the page in updateGeneralPreferences
                    if (!error && prefs.generalPreferences) {
                        // if error, we were refreshing the page, and it was silently failing
                        window.location.reload();
                    }
                })
            );
        })
    );

    @ViewChild('scrollPanel') scrollPanel: ScrollPanelComponent;

    @ViewChild('monitoringPrefs', { static: false }) set monitoringPrefs(component: MonitorUserPreferencesComponent) {
        if (component) {
            this.monitoringPrefsComponent = component;
        }
    };
    @ViewChild('targetingPrefs', { static: false }) set targetingPrefs(component: DiscoverUserPreferencesComponent) {
        if (component) {
            this.targetingPrefsComponent = component;
        }
    };
    @ViewChild('reportPrefs', { static: false }) set reportPrefs(component: ReportUserPreferencesComponent) {
        if (component) {
            this.reportPrefsComponent = component;
        }
    };
    @ViewChild('generalPrefs', { static: false }) set generalPrefs(component: GeneralPreferencesComponent) {
        if (component) {
            this.generalPrefsComponent = component;
        }
    };

    constructor(
        private currentUserStateService: CurrentUserStateService,
        private userPreferencesService: UserPreferencesService,
        private titleService: Title,
        private localizationService: LocalizationService,
        private cd: ChangeDetectorRef,
        private coreDialogService: CoreDialogService,
        private route: ActivatedRoute,
        private router: Router
    ) {
        this.subscriptions.push(
            this.currentUserStateService.getPermissions().subscribe(permissions => {
                if (permissions) {
                    this.hasTargeting = permissions.includes(userPermissionsEnum.advancedTargeting);
                    this.hasMonitoring = permissions.includes(userPermissionsEnum.advancedMonitoring);
                    const socialPermissions = [
                        userPermissionsEnum.socialIntelligence,
                        userPermissionsEnum.advancedMonitoring
                    ];
                    this.hasReports = socialPermissions.some(p => permissions.includes(p));

                    requestAnimationFrame(() => {
                        // Initially select the first tab by default
                        this.tabIndexChange(0);
                    });
                }
            }),
            this.currentUserStateService.getUILanguageTag().subscribe(x => {
                requestAnimationFrame(() => {
                    // Initially select the first tab by default
                    this.tabIndexChange(0);
                });

            }),
            this.userPreferencesService.loading$.subscribe(x => {
                this.loading = x;
            }),
            this.userPreferencesService.getUserAppPreferences().subscribe(userAppPrefs => {
                this.userAppPreferences = userAppPrefs;
                this.cachedUserAppPreferences = userAppPrefs;
                this.isDirty = false;
            }),
            this.currentUserStateService.getPreferences().pipe(
                filter(x => !!x)
            ).subscribe(x => {
                this.show = true;
                const newpref = new PreferencesModel();

                const uiLan = new DataLanguageModel();
                const refLan = new DataLanguageModel();
                uiLan.id = x.uiLanguage.id;
                uiLan.languageTag = x.uiLanguage.languageTag;
                uiLan.name = x.uiLanguage.name;
                refLan.id = x.referenceDataLanguage.id;
                refLan.languageTag = x.referenceDataLanguage.languageTag;
                refLan.name = x.referenceDataLanguage.name;

                newpref.timeFormat = x.timeFormat;
                newpref.dateFormat = x.dateFormat;
                newpref.dateFormatId = x.dateFormatId;
                newpref.timeZone = x.timeZone;
                newpref.uiLanguage = uiLan;
                newpref.referenceDataLanguage = refLan;
                newpref.tosAccepted = x.tosAccepted;
                newpref.currency = x.currency;
                newpref.notificationPreference = x.notificationPreference;
                this.generalPreferences = newpref;
            }),

        );
        this.titleService.setTitle(
            this.localizationService.transform(
                'Account.userPrefs.browserTitle'
            )
        );
    }

    ngOnInit(): void {
        this.subscriptions.push(
            // A save that only changed the agent warning preference does not reload the page, so
            // the button has to be settled here
            this.saveStream$.subscribe(() => this.updateDirtyFlag())
        );
    }

    ngAfterViewInit(): void {
        this.subscriptions.push(
            this.route.fragment.pipe(
                filter(frag => !!frag && frag === FAVORITE_COLORS_SECTION_ID),
                tap(() => this.tabIndexChange(0)),
                debounceTime(500)
            ).subscribe(frag => {
                const targetElement: HTMLElement | null = document.querySelector(`#${frag}`)!;
                if (targetElement) {
                    this.scrollPanel.scrollIntoViewNative(targetElement);
                }
                requestAnimationFrame(() => {
                    this.router.navigate([], {
                        fragment: undefined,
                        replaceUrl: true
                    });
                });
            })
        );
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    updateDirtyFlag() {
        this.checkUserPreferencesDirty();
        const currentGeneralPrefs = this.generalPrefsComponent?.getUpdatedValues();
        this.generalPreferencesDirty = currentGeneralPrefs && !deepCompare(currentGeneralPrefs, this.generalPreferences, { emptyChecker: 'all' });
        const footerDirty = this.targetingPrefsComponent?.isFooterDirty();
        // The automation agent warning preference lives in agents-service rather than in the
        // preferences record, so the deep compare above cannot see it
        const automationAgentPrefsDirty = this.generalPrefsComponent?.automationAgentQuotaWarningsDirty;

        this.isDirty = (
            footerDirty
            || this.appPreferencesDirty
            || this.generalPreferencesDirty
            || automationAgentPrefsDirty
        );

        if (this.appPreferencesDirty) {
            this.userAppPreferences = this.getLatestUserAppPreferences();
        }
    }

    checkUserPreferencesDirty() {
        const userPrefs = this.getLatestUserAppPreferences();
        if (!userPrefs) {
            return;
        }

        let discoverPreferencesDirty = false;
        let monitorPreferencesDirty = false;
        let reportPreferencesDirty = false;
        let generalPrefsDirty = false;

        if (this.targetingPrefsComponent) {
            discoverPreferencesDirty = userPrefs.targetingPrefs && !deepCompare(userPrefs.targetingPrefs, this.cachedUserAppPreferences.targetingPrefs,
                { emptyChecker: 'all' });
        }
        if (this.monitoringPrefsComponent) {
            monitorPreferencesDirty = userPrefs.monitoringPrefs && !deepCompare(userPrefs.monitoringPrefs, this.cachedUserAppPreferences.monitoringPrefs,
                { emptyChecker: 'all' });
        }
        if (this.reportPrefsComponent) {
            reportPreferencesDirty = userPrefs.reportsPrefs && !deepCompare(userPrefs.reportsPrefs, this.cachedUserAppPreferences.reportsPrefs,
                { emptyChecker: 'all' });
        }
        if (this.generalPrefsComponent) {
            generalPrefsDirty = userPrefs.generalPrefs && !deepCompare(
                userPrefs.generalPrefs,
                this.cachedUserAppPreferences.generalPrefs
            );
        }

        this.appPreferencesDirty = discoverPreferencesDirty || monitorPreferencesDirty || reportPreferencesDirty || generalPrefsDirty;
    }

    getLatestUserAppPreferences(): UserAppPreferencesModel {
        const currentTargPrefs = this.targetingPrefsComponent?.getUpdatedValues();
        const currentMonPrefs = this.monitoringPrefsComponent?.getUpdatedValues();
        const currentReportPrefs = this.reportPrefsComponent?.getUpdatedValues();
        const currentGeneralPrefs = this.generalPrefsComponent?.getUpdatedGeneralPrefs();

        const userAppPrefs = new UserAppPreferencesModel();
        if (currentMonPrefs) {
            userAppPrefs.monitoringPrefs = currentMonPrefs;
        }
        if (currentTargPrefs) {
            userAppPrefs.targetingPrefs = currentTargPrefs;
        } else if (this.userAppPreferences?.targetingPrefs) {
            userAppPrefs.targetingPrefs = this.userAppPreferences.targetingPrefs;
        }
        if (currentReportPrefs) {
            userAppPrefs.reportsPrefs = currentReportPrefs;
        } else if (this.userAppPreferences?.reportsPrefs) {
            userAppPrefs.reportsPrefs = this.userAppPreferences.reportsPrefs;
        }
        if (this.cachedUserAppPreferences?.socialPrefs) {
            userAppPrefs.socialPrefs = this.cachedUserAppPreferences.socialPrefs;
        } else if (this.userAppPreferences?.socialPrefs) {
            userAppPrefs.socialPrefs = this.userAppPreferences.socialPrefs;
        }
        if (currentGeneralPrefs) {
            userAppPrefs.generalPrefs = currentGeneralPrefs;
        } else if (this.userAppPreferences?.generalPrefs) {
            userAppPrefs.generalPrefs = this.userAppPreferences.generalPrefs;
        }
        if (!currentTargPrefs && !currentMonPrefs && !currentReportPrefs && !currentGeneralPrefs && this.selectedTabId !== this.tabIds.GENERAL) {
            console.error('user preferences are null');
            return undefined;
        }
        return userAppPrefs;
    }

    saveChanges() {
        if (this.childErrors.length) {
            this.scrollToSection(this.childErrors[0]);
            return;
        }

        const currentGeneralPrefs = this.generalPrefsComponent?.getUpdatedValues();
        this.saveAction$.next(
            {
                appPreferences: (this.appPreferencesDirty || this.targetingPrefsComponent?.isFooterDirty()) ? this.getLatestUserAppPreferences() : undefined,
                generalPreferences: this.generalPreferencesDirty ? currentGeneralPrefs : undefined
            }
        );
    }

    scrollToSection(section: ElementRef, event?: any) {
        if (event instanceof KeyboardEvent && !(event.key === ActionKeys.ENTER || event.key === ActionKeys.SPACE)) {
            return;
        }
        const elRect = section.nativeElement.getBoundingClientRect();
        const spEl = this.scrollPanel.sp.contentViewChild.nativeElement;
        const spRect = spEl.getBoundingClientRect();
        const top = Math.max(elRect.top - spRect.top + spEl.scrollTop);
        this.scrollPanel.scrollTo(top);
    }

    onChildError(errorRefs: ElementRef[]) {
        this.childErrors.push(...errorRefs)
    }

    onChildRemoveError(errorRefs: ElementRef[]) {
        this.childErrors = this.childErrors.filter(
            err => !errorRefs.includes(err)
        );
    }

    updateSectionListItems(key: UserPrefsListItemsKey, listItems: SectionListItems) {
        this[key] = listItems;
        this.cd.detectChanges();
    }

    resetUserPreferences() {
        const confirmDialogModel: ConfirmDialogModel = {
            inputs: {
                heading: 'UserPreferences.reset.heading',
                message: 'UserPreferences.reset.message',
                showCancel: true,
                okLabel: 'UserPreferences.reset'
            },
            outputs: {
                dialogClose: (confirmed: boolean) => {
                    if (confirmed) {
                        this.reset();
                    }
                }
            }
        };
        this.coreDialogService.open(ConfirmDialogComponent, confirmDialogModel);
    }

    reset() {
        const defaultUserAppPreferencesModel = new UserAppPreferencesModel();
        // reset the defaultBriefingTemplate to BRIEFING_1 because it is empty string
        defaultUserAppPreferencesModel.monitoringPrefs.briefingsPrefs.defaultBriefingTemplate = 'BRIEFING_1';
        this.userAppPreferences = defaultUserAppPreferencesModel;
        this.generalPreferences.currency = UserCurrency.USD;

        const defaultLanguageOptions = this.userPreferencesService.getDefaultLanguageOptions();
        this.generalPreferences.uiLanguage = defaultLanguageOptions.uiLanguage;
        this.generalPreferences.referenceDataLanguage = defaultLanguageOptions.referenceDataLanguage;

        this.userPreferencesService.defaultDistributionFooter = new FooterModel();
        this.saveAction$.next(
            {
                appPreferences: this.userAppPreferences,
                generalPreferences: this.generalPreferences,
                userAppMessageTypeEnum: UserAppMessageTypeEnum.RESET
            }
        );
    }

    tabIndexChange(index: number): void {
        this.selectedTabIndex = index;
    }

    tabChange(tab: TabComponent): void {
        this.selectedTabId = tab.id;
        this.scrollPanel?.scrollTo(0, 0, false);
    }
}
